import { Component, computed, effect, inject, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule, Sort, SortDirection } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { Network, Period, ReviewData, ReviewsRequest } from '@models/online-reputation.models';
import { FilterStore } from '@core/stores/filter.store';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { DatePipe, SlicePipe } from '@angular/common';
import { ReplyFormComponent } from './reply-form/reply-form.component';
import { TagFormComponent } from './tag-form/tag-form.component';
import { Dispatcher } from '@ngrx/signals/events';
import { loadMoreReviews, loadReviews } from '@core/events/reviews.events';
import { ReviewsStore } from '@core/stores/reviews.store';
import { distinctUntilChanged, filter, map, startWith } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { ApplicationStore } from '@core/stores/application.store';
import { ReviewsService } from '@core/services/reviews.service';

@Component({
  selector: 'app-reviews',
  templateUrl: './reviews.component.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatTableModule,
    MatSortModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    DatePipe,
    MatTooltipModule,
    SlicePipe,
    MatProgressBar,
    MatButton,
    MatIconButton,
    MatDialogModule,
    TranslatePipe
  ]
})
export class ReviewsComponent {
  displayedColumns: string[] = [
    'commentedAt',
    'rating',
    'response',
    'comment',
    'respondedAt',
    'locationId',
    'authorName',
    'isResponded',
    'tag'
  ];
  dataSource = new MatTableDataSource<ReviewData>([]);
  totalElements = 0;
  pageSize = 10;
  pageToken?: string;
  sortActive = 'commentedAt';
  sortDirection: SortDirection = 'desc';
  loading = false;

  @ViewChild(MatSort) sort!: MatSort;

  readonly filterStore = inject(FilterStore);
  readonly reviewsStore = inject(ReviewsStore);
  readonly dispatcher = inject(Dispatcher);
  readonly router = inject(Router);
  readonly syncing = signal(false);

  private readonly dialog = inject(MatDialog);
  private readonly applicationStore = inject(ApplicationStore);
  private readonly reviewsService = inject(ReviewsService);

  readonly canSyncReviews = computed(() =>
    this.applicationStore.userPermissions().includes('OREP_SYNC_REVIEWS')
  );

  constructor() {
    // Keep local UI state in sync with reviews store updates.
    effect(() => {
      this.totalElements = this.reviewsStore.totalElements();
      this.pageToken = this.reviewsStore.pageToken();
      this.loading = this.reviewsStore.loading();
      this.dataSource.data = this.reviewsStore.reviews();
      this.applySort();
    });

    this.router.events
    .pipe(
      startWith(this.router.url),
      filter((event) => typeof event === 'string' || event instanceof NavigationEnd),
      map(() => this.router.url),
      distinctUntilChanged(),
      takeUntilDestroyed(),
    )
    .subscribe(() => {
      this.fetchReviews();
    });
  }

  openReplyForm(review: ReviewData): void {
    const filter = this.filterStore.filter();
    const network = filter.network;

    this.dialog.open(ReplyFormComponent, {
      width: '840px',
      maxWidth: '95vw',
      data: {
        review,
        network
      }
    }).afterClosed().subscribe((result?: { submitted?: boolean }) => {
      if (result?.submitted) {
        this.fetchReviews();
      }
    });
  }

  openTagsForm(review: ReviewData) {
    this.dialog.open(TagFormComponent, {
      width: '420px',
      maxWidth: '95vw',
      data: {
        review: review,
      }
    }).afterClosed().subscribe((result?: { submitted?: boolean }) => {
      // if (result?.submitted) {
      //   this.fetchReviews();
      // }
    });
  }

  syncReviews(): void {
    this.syncing.set(true);
    const filter = this.filterStore.filter();
    const request = this.buildRequest(
      filter.network as Network,
      filter.period as Period
    );
    this.reviewsService.syncReviews(request).subscribe({
      next: () => {
        this.syncing.set(false);
        this.fetchReviews();
      },
      error: () => {
        this.syncing.set(false);
      }
    });
  }

  fetchReviews(append = false): void {
    const filter = this.filterStore.filter();
    const request = this.buildRequest(
      filter.network as Network,
      filter.period as Period,
      append ? this.pageToken : undefined
    );

    if (append) {
      if (!request.pageToken) {
        return;
      }
      this.dispatcher.dispatch(loadMoreReviews(request));
      return;
    }

    this.dispatcher.dispatch(loadReviews(request));
  }

  private buildRequest(network: Network | undefined, period: Period | undefined, pageToken?: string): ReviewsRequest {
    return {
      network: network || {} as Network,
      period: period || {} as Period,
      pageToken,
      limit: this.pageSize
    };
  }

  sortData(sort: Sort): void {
    this.sortActive = sort.active;
    this.sortDirection = sort.direction;
    this.applySort();
  }

  applySort(): void {
    const data = this.dataSource.data.slice();
    if (!this.sortActive || this.sortDirection === '') {
      this.dataSource.data = data;
      return;
    }

    const directionMultiplier = this.sortDirection === 'asc' ? 1 : -1;

    this.dataSource.data = data.sort((a, b) => {
      switch (this.sortActive) {
        case 'commentedAt': {
          return this.compareNullableDates(a.commentedAt, b.commentedAt, directionMultiplier);
        }
        case 'respondedAt': {
          return this.compareNullableDates(a.respondedAt, b.respondedAt, directionMultiplier);
        }
        case 'rating': {
          return this.compareNullableNumbers(a.rating, b.rating, directionMultiplier);
        }
        default:
          return 0;
      }
    });
  }

  private compareNullableDates(
    valueA: Date | string | null | undefined,
    valueB: Date | string | null | undefined,
    directionMultiplier: number
  ): number {
    const timestampA = this.toTimestamp(valueA);
    const timestampB = this.toTimestamp(valueB);

    if (timestampA == null && timestampB == null) {
      return 0;
    }

    if (timestampA == null) {
      return 1;
    }

    if (timestampB == null) {
      return -1;
    }

    return (timestampA - timestampB) * directionMultiplier;
  }

  private compareNullableNumbers(
    valueA: number | null | undefined,
    valueB: number | null | undefined,
    directionMultiplier: number
  ): number {
    if (valueA == null && valueB == null) {
      return 0;
    }

    if (valueA == null) {
      return 1;
    }

    if (valueB == null) {
      return -1;
    }

    return (valueA - valueB) * directionMultiplier;
  }

  private toTimestamp(value: Date | string | null | undefined): number | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value.getTime();
    }

    const timestamp = new Date(value).getTime();
    return Number.isNaN(timestamp) ? null : timestamp;
  }
}
